import { siteApi } from "@api";
import { ClaimButtonRef } from "@components/inputs/ClaimButton";
import { FlyingCoinsRef } from "@components/utils/FlyingCoins";
import { toast } from "@components/utils/Toaster";
import { SEON_FEATURE } from "@configs/seon";
import { useStatsigTracking } from "@configs/statsig";
import { Paths } from "@constants/paths";
import { UserMode } from "@types";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { APIErrorCodes, WalletTxMethod, bnOrZero, useHandleApiResponse } from "kz-ui-sdk";
import { throttle } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useAuthSession from "./useAuthSession";
import useProfileBalance from "./useProfileBalance";
import usePromotionStatus from "./usePromotionStatus";
import { useSeonSession } from "./useSeonSession";

export interface UseDailyRewardClaimOptions {
  /**
   * Callback when claim button is clicked (after animations start)
   * Use this to handle navigation, balance refresh, etc.
   */
  onClaimClick?: () => void;
  /**
   * Callback when reward is successfully claimed via API
   */
  onClaimSuccess?: (claimedAmount: number) => void;
  /**
   * Callback when claim fails
   */
  onClaimError?: () => void;
  /**
   * Whether to auto-claim on mount (default: true)
   */
  autoClaimOnMount?: boolean;
}

export interface UseDailyRewardClaimResult {
  // State
  claimedPoint: number | null;
  isBtnClaimable: boolean;
  isClaimingAnimationActive: boolean;
  isButtonVisible: boolean;
  isWheelAnimated: boolean;
  isSlotAnimated: boolean;

  // Refs
  refFlyingCoins: React.RefObject<FlyingCoinsRef>;
  refClaimButton: React.RefObject<ClaimButtonRef>;

  // Functions
  fetchClaimReward: () => Promise<void>;
  handleClaim: () => void;

  // Config
  flyingCoinConfig: {
    condition: number;
    flyingCoins: number;
  };

  // Loading state
  isClaiming: boolean;
}

const FLYING_COINS_CONFIGS = [
  {
    condition: 10000,
    flyingCoins: 15,
  },
  {
    condition: 1000,
    flyingCoins: 12,
  },
  {
    condition: 100,
    flyingCoins: 9,
  },
  {
    condition: 50,
    flyingCoins: 7,
  },
  {
    condition: 0.01,
    flyingCoins: 5,
  },
];

export function useDailyRewardClaim(options: UseDailyRewardClaimOptions = {}): UseDailyRewardClaimResult {
  const { onClaimClick, onClaimSuccess, onClaimError, autoClaimOnMount = true } = options;

  const refFlyingCoins = useRef<FlyingCoinsRef>(null);
  const refClaimButton = useRef<ClaimButtonRef>(null);
  const { userMode } = useAuthSession();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refetch: refetchBalance } = useProfileBalance();
  const { isDailyBonusClaimable, isDailyBonusAvailable } = usePromotionStatus();
  const { handleApiResponse } = useHandleApiResponse({
    toast,
  });
  const { trackPromoClaimAttempt, trackPromoClaimSuccess } = useStatsigTracking();

  const [refetchRewardStatus] = siteApi.useLazyGetRewardsStatusQuery();
  const [claimReward, result] = siteApi.useClaimRewardMutation();
  const { getSession: getSeonSession } = useSeonSession(SEON_FEATURE.PROMOTION_DAILY_LOGIN, {
    autoInit: true,
  });

  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [isWheelAnimated, setIsWheelAnimated] = useState(false);
  const [isSlotAnimated, setIsSlotAnimated] = useState(false);
  const [claimedPoint, setClaimedPoint] = useState<number | null>(null);
  const [isBtnClaimable, setIsBtnClaimable] = useState(true);
  const [isClaimingAnimationActive, setIsClaimingAnimationActive] = useState(false);

  const animationTimeoutRefs = useRef<{
    displayWheel: NodeJS.Timeout | null;
    displaySlotReward: NodeJS.Timeout | null;
    wheelTimeout: NodeJS.Timeout | null;
  }>({
    displayWheel: null,
    displaySlotReward: null,
    wheelTimeout: null,
  });

  const startAnimations = useCallback(() => {
    // Clear any existing timeouts
    if (animationTimeoutRefs.current.displayWheel) {
      clearTimeout(animationTimeoutRefs.current.displayWheel);
    }
    if (animationTimeoutRefs.current.displaySlotReward) {
      clearTimeout(animationTimeoutRefs.current.displaySlotReward);
    }
    if (animationTimeoutRefs.current.wheelTimeout) {
      clearTimeout(animationTimeoutRefs.current.wheelTimeout);
    }

    // Reset animation states
    setIsWheelAnimated(false);
    setIsSlotAnimated(false);
    setIsButtonVisible(false);

    // Start animation sequence
    animationTimeoutRefs.current.displaySlotReward = setTimeout(() => {
      setIsSlotAnimated(true);
    }, 1500);

    animationTimeoutRefs.current.wheelTimeout = setTimeout(() => {
      setIsButtonVisible(true);
    }, 7000);

    animationTimeoutRefs.current.displayWheel = setTimeout(() => {
      setIsWheelAnimated(true);
    }, 7200);
  }, []);

  useEffect(() => {
    // Only start animations when eligible and claimable
    if (isDailyBonusAvailable && isDailyBonusClaimable) {
      startAnimations();
    }

    // Capture current refs for cleanup
    const timeouts = animationTimeoutRefs.current;

    return () => {
      if (timeouts.displayWheel) {
        clearTimeout(timeouts.displayWheel);
        timeouts.displayWheel = null;
      }
      if (timeouts.displaySlotReward) {
        clearTimeout(timeouts.displaySlotReward);
        timeouts.displaySlotReward = null;
      }
      if (timeouts.wheelTimeout) {
        clearTimeout(timeouts.wheelTimeout);
        timeouts.wheelTimeout = null;
      }
    };
  }, [isDailyBonusAvailable, isDailyBonusClaimable, startAnimations]);

  const fetchClaimReward = useCallback(async () => {
    // check if user is in open mode
    if (userMode !== UserMode.MEMBER) {
      // ignore the claim
      return;
    }

    // Check if the rewards is available
    if (!isDailyBonusAvailable) {
      return;
    }

    // check if the reward is claimable
    if (!isDailyBonusClaimable) {
      toast.error(t("already claimed for today please come back tomorrow"), {
        id: "daily-bonus-already-claimed-toast",
      });
      if (onClaimError) {
        onClaimError();
      } else {
        navigate(Paths.PRIVATE.HOME, { replace: true });
      }
      return;
    }

    trackPromoClaimAttempt(WalletTxMethod.DailyBonus);

    const seonSession = await getSeonSession();
    const result = await claimReward({ query: { type: WalletTxMethod.DailyBonus }, seonSession });
    handleApiResponse(result, {
      onSuccess: () => {
        trackPromoClaimSuccess(WalletTxMethod.DailyBonus);
        const claimedAmount = Number(result?.data?.model.reqAmount);
        setClaimedPoint(claimedAmount);
        onClaimSuccess?.(claimedAmount);
      },
      onError: () => {
        if (onClaimError) {
          onClaimError();
        } else {
          // Redirect to home page
          navigate(Paths.PRIVATE.HOME, { replace: true });
        }
      },
      errorMessageResolver: (error) => {
        if (error?.data?.error?.message === APIErrorCodes.ClaimReward.UnableRedeem) {
          return t("daily bonus is not available");
        }
        return resolveAuthResponseError(error, t);
      },
    });
  }, [
    userMode,
    isDailyBonusAvailable,
    isDailyBonusClaimable,
    t,
    navigate,
    getSeonSession,
    claimReward,
    handleApiResponse,
    trackPromoClaimAttempt,
    trackPromoClaimSuccess,
    onClaimSuccess,
    onClaimError,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledClaim = useCallback(throttle(fetchClaimReward, 1000, { leading: true, trailing: false }), []);

  // Track previous eligibility state to detect transitions
  const prevEligibilityRef = useRef(isDailyBonusAvailable);

  // Watch for eligibility changes and trigger claim when becoming eligible
  useEffect(() => {
    const wasIneligible = !prevEligibilityRef.current;
    const isNowEligible = isDailyBonusAvailable;

    // If it changed from ineligible to eligible, reset states and trigger claim
    // Only if autoClaimOnMount is enabled to avoid conflicts with mount effect
    if (wasIneligible && isNowEligible && isDailyBonusClaimable && autoClaimOnMount) {
      // Reset claim-related states for fresh start
      setClaimedPoint(null);
      setIsBtnClaimable(true);
      fetchClaimReward();
      prevEligibilityRef.current = isDailyBonusAvailable;
    } else {
      // Update ref even if not transitioning
      prevEligibilityRef.current = isDailyBonusAvailable;
    }
  }, [isDailyBonusAvailable, isDailyBonusClaimable, fetchClaimReward, autoClaimOnMount]);

  // Auto-claim on mount if enabled and reward is claimable
  // Only trigger once per mount cycle
  useEffect(() => {
    if (autoClaimOnMount && isDailyBonusAvailable && isDailyBonusClaimable) {
      throttledClaim();
    }
  }, [throttledClaim, autoClaimOnMount, isDailyBonusAvailable, isDailyBonusClaimable]);

  const handleClaim = useCallback(() => {
    refFlyingCoins.current?.start();
    setIsBtnClaimable(false);
    setIsClaimingAnimationActive(true);

    // Trigger balance refresh
    refetchBalance();
    refetchRewardStatus();

    // Call custom callback if provided
    onClaimClick?.();
  }, [refetchBalance, refetchRewardStatus, onClaimClick]);

  const flyingCoinConfig = useMemo(() => {
    // find the first level that the balance is greater than or equal to
    const balanceBn = bnOrZero(claimedPoint);
    for (const level of FLYING_COINS_CONFIGS) {
      if (balanceBn.gte(level.condition)) {
        return level;
      }
    }
    return {
      condition: 0,
      flyingCoins: 0,
    };
  }, [claimedPoint]);

  return {
    // State
    claimedPoint,
    isBtnClaimable,
    isClaimingAnimationActive,
    isButtonVisible,
    isWheelAnimated,
    isSlotAnimated,

    // Refs
    refFlyingCoins,
    refClaimButton,

    // Functions
    fetchClaimReward,
    handleClaim,

    // Config
    flyingCoinConfig,

    // Loading state
    isClaiming: result.isLoading,
  };
}
